/**
 * @file src/pages/payment-management/components/ModeFilterTabs.jsx
 * @description Pill-style filter tabs for narrowing payments by mode
 * (Cash, Bank Deposit, etc). Always shows an "All" pill first.
 *
 * Modes with zero payments are hidden — there's no point in offering
 * a filter that would return an empty list.
 *
 * Pure presentational. Parent owns the active filter state.
 */

import React from "react";
import { useTranslation } from "react-i18next";

const ModeFilterTabs = ({
  modes,
  modeCounts,
  totalCount,
  active,
  onChange,
}) => {
  const { t } = useTranslation("pages");
  const activeModes = modes.filter((m) => modeCounts[m] > 0);

  return (
    <div className="flex flex-wrap gap-2">
      <FilterPill
        label={t("common.all")}
        count={totalCount}
        isActive={active === "all"}
        onClick={() => onChange("all")}
      />
      {activeModes.map((mode) => (
        <FilterPill
          key={mode}
          label={t(`paymentModes.${mode}`, { defaultValue: mode })}
          count={modeCounts[mode]}
          isActive={active === mode}
          onClick={() => onChange(mode)}
        />
      ))}
    </div>
  );
};

const FilterPill = ({ label, count, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? "bg-primary text-primary-foreground shadow-md"
        : "bg-card text-muted-foreground hover:bg-muted border border-border"
    }`}
  >
    {label}
    <span
      className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
        isActive
          ? "bg-white/20 text-primary-foreground"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {count}
    </span>
  </button>
);

export default ModeFilterTabs;
